import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

import PropTypes from "prop-types";
import React from "react";
import autoBind from "react-autobind";
import LeadDrawerActions from "~/actions/lead_drawer_actions";
import ErrorMessage from "~/components/forms/ErrorMessage";
import SmsTextEditor from "~/components/forms/HtmlEditors/SmsTextEditor";
import SmsTemplateSelect from "~/components/forms/Templates/SmsTemplateSelect";
import InsertDocumentButton from "~/components/insert_document_button";
import UnsupportedVariablesWarning from "~/components/modals/UnsupportedVariablesWarning";
import ForwardingNumberGuidance from "~/components/NotificationSms/ForwardingNumberGuidance";
import A2pNotApprovedWarning from "~/components/warnings/A2pNotApprovedWarning";
import VerifiedTextingWarning from "~/components/warnings/VerifiedTextingWarning";
import { checkFeatureFlag } from "~/helpers/FeatureFlagChecker";
import LeadHelpers from "~/helpers/lead_helpers";
import { normalizeUnsupportedVariablesError } from "~/lib/unsupportedVariables";
import TextMessageFormOnboarding from "~/pages/PhoneNumberSettings/components/TextMessageFormOnboarding";
import LeadDrawerStore from "~/stores/lead_drawer_store";
import FormFooter from "./common/form_footer";

const MAX_MEDIA_SIZE = 5120000; // 5MB
const charsLimit = 1500;
const limitError = [
  "You have exceeded your limit of maximum characters allowed.",
];

let leadStoreListener;

class TextMessageForm extends React.Component {
  isRestrictionForUnverifiedNumbersEnabled = checkFeatureFlag(
    process.env.SHAKAPACKER_PUBLIC_BLOCK_UNVERIFIED_NUMBERS_FLAG,
  );

  constructor(props) {
    super(props);

    const { lead, schedule, smsMessage } = this.props;
    const { currentUser } = Rails.helpers;

    this.state = {
      id: smsMessage?.id || null,
      status: lead?.status,
      errors: {},
      body: smsMessage?.body || "",
      media: smsMessage?.media || null,
      media_url: smsMessage?.media_url || null,
      schedule: schedule || {
        customOptions: { timezone: currentUser.timezone },
      },
      scheduleSmsMessageAllowed: checkFeatureFlag(
        process.env.SHAKAPACKER_PUBLIC_ALLOW_SCHEDULE_TEXTS_FLAG,
      ),
      variablesWarning: null,
      submittingSaveAnyway: false,
    };

    autoBind(this);
  }

  componentDidMount() {
    const { smsMessage, schedule } = this.props;

    // listen to LeadDrawerStore changes
    leadStoreListener = LeadDrawerStore.addListener(this.onLeadStoreChange);

    this.setState((prevState) => ({
      smsMessage: { ...prevState.smsMessage, ...smsMessage },
      schedule: { ...prevState.schedule, ...schedule },
    }));
  }

  componentDidUpdate(prevProps) {
    const { smsMessage, schedule } = this.props;

    if (
      schedule !== prevProps.schedule ||
      smsMessage?.id !== prevProps.smsMessage?.id
    ) {
      this.setState((prevState) => ({
        smsMessage: { ...prevState.smsMessage, ...smsMessage },
        schedule,
      }));
    }
  }

  componentWillUnmount() {
    // remove listener to LeadDrawerStore changes on Unmount
    if (leadStoreListener) {
      leadStoreListener.remove();
    }
  }

  onLeadStoreChange() {
    const { leadDrawerStoreAction } = LeadDrawerStore.getState();

    if (
      leadDrawerStoreAction === "createLeadSmsMessageFail" ||
      leadDrawerStoreAction === "updateLeadSmsMessageFail"
    ) {
      this.setState({ sending: false });
    }
  }

  handleBodyChange = (text) => {
    const overLimit = text && charsLimit < text.length;

    this.setState((prevState) => {
      const { body: _removed, ...restErrors } = prevState.errors;

      return {
        body: text,
        errors: overLimit ? { ...restErrors, body: limitError } : restErrors,
        disabledButton: !!overLimit,
      };
    });
  };

  handleMediaUrlChange = (media) => {
    if (!media) {
      return;
    }

    this.setState({
      media,
      media_url: media.small_media_url,
    });
  };

  variableWarningSubmitOptions = () => ({
    onUnsupportedVariables: (warning) => {
      this.footer?.resetSending?.();
      this.setState({
        variablesWarning: normalizeUnsupportedVariablesError(warning),
        submittingSaveAnyway: false,
        sending: false,
      });
    },
    onDone: () => {
      this.setState({
        variablesWarning: null,
        submittingSaveAnyway: false,
        sending: false,
      });
    },
    onFail: () => {
      this.footer?.resetSending?.();
      this.setState({
        variablesWarning: null,
        submittingSaveAnyway: false,
        sending: false,
      });
    },
  });

  onFormSubmit(e, saveAnyway = false) {
    e.persist();

    const { body, smsNumber, media_url, schedule, id } = this.state;
    const { lead } = this.props;

    this.setState({ sending: true });

    const errors = this.validate();

    if (Object.keys(errors).length === 0) {
      const _footer = this.footer.serialize();
      const _attrs = Object.assign(_footer, {
        sms_message: {
          body,
          media_url,
          schedule,
          internal: _footer.internal,
        },
        sms_number_id: smsNumber.id,
        save_anyway: saveAnyway,
      });

      if (id) {
        LeadDrawerActions.updateLeadSmsMessage(
          lead,
          id,
          _attrs,
          this.variableWarningSubmitOptions(),
        );
      } else {
        LeadDrawerActions.createLeadSmsMessage(
          lead,
          _attrs,
          this.variableWarningSubmitOptions(),
        );
      }
    } else {
      this.setState({ sending: false, errors });
    }
  }

  setSchedule(newSchedule) {
    this.setState((prevState) => ({
      schedule: { ...prevState.schedule, ...newSchedule },
    }));
  }

  validate() {
    const { body } = this.state;
    let errors = {};

    if (!body) {
      errors.body = ["Can't be empty."];
    }

    if (body && charsLimit < body.length) {
      errors.body = limitError;
    }

    errors = this.footer.validate(errors);

    this.setState({ errors });

    return errors;
  }

  applyTemplate(template) {
    const { body } = this.state;
    let smsAttrs;

    if (template) {
      smsAttrs = {
        body: template.body,
        templateBody: template.body,
        media: {
          name: template.media_name,
        },
        media_url: template.media_url,
        errors: {},
      };
    } else {
      smsAttrs = {
        body: null,
        templateBody: null,
        media: null,
        media_url: null,
        errors: {},
      };
    }

    this.setState({
      body,
      ...smsAttrs,
    });
  }

  renderAttachment = () => {
    const { media, media_url } = this.state;

    if (!media_url) {
      return null;
    }

    const name = media?.name || media?.title;

    return (
      <div className="alert alert-success p10 text-left mb5" key={name}>
        <div className="pull-right">
          <a
            href="#remove-attachment"
            onClick={(e) => {
              e.preventDefault();
              this.setState({ media: null, media_url: null });
            }}
          >
            <FontAwesomeIcon icon={["far", "fa-times"]} />
          </a>
        </div>
        <div className="text-break">
          <FontAwesomeIcon icon={["far", "fa-file"]} className="mr10" />
          <a href={media_url} target="_blank" rel="noopener noreferrer">
            {name}
          </a>
        </div>
      </div>
    );
  };

  render() {
    const {
      errors,
      sending,
      disabledButton,
      body,
      templateBody,
      smsNumber,
      subscription,
      status,
      smsMessage,
      internal,
      media_url,
      media,
      schedule,
      scheduleSmsMessageAllowed,
      linksToInsert,
      variablesWarning,
      submittingSaveAnyway,
    } = this.state;
    const { lead, scheduleBtnPopoverPlacement, surface } = this.props;
    const mediaName = media?.name || media?.title;
    const restrictedUnverifiedNumbers =
      this.isRestrictionForUnverifiedNumbersEnabled;
    const showVerifiedTextingWarning =
      restrictedUnverifiedNumbers && !smsNumber?.verified_texting;
    const showA2PWarning =
      restrictedUnverifiedNumbers &&
      smsNumber?.verified_texting &&
      !smsNumber?.a2p_approved;

    if (subscription && smsNumber?.service_number) {
      return (
        <>
          <form onSubmit={this.onFormSubmit}>
            <ForwardingNumberGuidance surface={surface} smsNumber={smsNumber} />
            {showVerifiedTextingWarning && <VerifiedTextingWarning />}
            {showA2PWarning && <A2pNotApprovedWarning />}

            <div className="form-group mb15">
              <label htmlFor="sms_text_service_number" className="d-block">
                From
              </label>
              <div
                id="service_number"
                className="form-control bg-grey-lightest"
              >
                {LeadHelpers.renderPhoneNumber(smsNumber.service_number)}
              </div>
            </div>

            <div className="form-group mb15">
              <SmsTextEditor
                mms={!!media_url}
                className={errors.body ? "tiny-mce has-error" : ""}
                placeholder="Enter your text message"
                onChange={this.handleBodyChange}
                handleMediaUrlChange={this.handleMediaUrlChange}
                value={body}
                leadId={this.props.lead?.id}
                replaceValue={templateBody}
                clearReplaceValue={() => {
                  this.setState({
                    templateBody: null,
                  });
                }}
                linksToInsert={linksToInsert}
                clearLinksToInsert={() => {
                  this.setState({ linksToInsert: [] });
                }}
                charsLimit={charsLimit}
              />
              {errors.body && <ErrorMessage message={errors.body} />}
            </div>

            <div className="row m0">
              <div className="col-lg-4 p0 pl0-sm-down">
                <InsertDocumentButton
                  setLinksToInsert={(newLinksToInsert) =>
                    this.setState({ linksToInsert: newLinksToInsert })
                  }
                />
              </div>

              <div className="col-lg-4 p0">
                {this.renderAttachment()}
                {errors.media && <ErrorMessage message={errors.media} />}
              </div>

              <div className="col-lg-4 p0 pl0-sm-down ml-auto">
                <SmsTemplateSelect
                  body={body}
                  media_url={media_url}
                  media_name={mediaName}
                  applyTemplate={this.applyTemplate}
                  validateSmsTemplate={this.validate}
                />
              </div>
            </div>

            <div className="mt15 mb15">
              <FormFooter
                ref={(el) => (this.footer = el)}
                activityType="direct_text_message"
                status={status}
                internal={internal}
                lead={lead}
                submitButtonText="Send Text Message"
                disabledButton={
                  sending || disabledButton || showVerifiedTextingWarning
                }
                onSubmit={this.onFormSubmit}
                showScheduleButton={scheduleSmsMessageAllowed}
                schedule={schedule}
                setSchedule={this.setSchedule}
                scheduleBtnPopoverPlacement={scheduleBtnPopoverPlacement}
                isEditingMode={!!smsMessage?.id}
              />
            </div>
          </form>
          <UnsupportedVariablesWarning
            show={!!variablesWarning}
            warning={variablesWarning}
            saving={submittingSaveAnyway}
            onReview={() => this.setState({ variablesWarning: null })}
            onSaveAnyway={() => {
              this.setState({ submittingSaveAnyway: true, sending: true });
              this.onFormSubmit(
                { persist: () => undefined, preventDefault: () => false },
                true,
              );
            }}
          />
        </>
      );
    }

    return (
      <TextMessageFormOnboarding
        onSubscriptionSelect={(sub) => {
          this.setState({
            subscription: sub,
          });
        }}
        onPhoneSelect={(num) => {
          this.setState({
            smsNumber: num,
          });
        }}
      />
    );
  }
}

TextMessageForm.defaultProps = {
  scheduleBtnPopoverPlacement: "bottom",
  lead: {},
  smsMessage: {},
  surface: null,
};

TextMessageForm.propTypes = {
  scheduleBtnPopoverPlacement: PropTypes.string,
  lead: PropTypes.shape({}),
  smsMessage: PropTypes.shape({}),
  surface: PropTypes.string,
};

export default TextMessageForm;
